import React from "react";
import moment from "moment";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import { Unit } from "../../../../../../services/management/units/api";

interface Props {
    document: Unit;
    onClick: () => void;
    active: boolean;
}

const STATUS_STYLES: Record<string, string> = {
    skipped: "bg-amber-100 text-amber-800",
    ready: "bg-amber-100 text-amber-800",
    processed: "bg-emerald-100 text-emerald-800",
    invalid: "bg-red-100 text-red-800",
};

function statusClass(status: string) {
    return STATUS_STYLES[status] ?? "bg-slate-100 text-slate-700";
}

export default function ListItem({ document, onClick, active }: Props) {
    const { t, locale } = useTranslation();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selection = window.getSelection();
        const anchor = selection?.anchorNode;
        if (anchor && selection?.toString().length && e.currentTarget.contains(anchor)) return;
        onClick();
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className={[
                "group w-full cursor-pointer select-text border-b border-border px-4 py-3 text-left transition-colors",
                "hover:bg-surface-3 focus:bg-surface-3 focus:outline-none",
                active ? "bg-accent/10 hover:bg-accent/10" : "",
            ].join(" ")}
        >
            <div className="grid grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1 @lg:grid-cols-[5rem_6rem_minmax(0,1fr)_auto] @lg:items-center">
                <div className="min-w-0 @lg:col-auto">
                    <div className="text-[10px] uppercase tracking-wide text-ink-muted @lg:hidden">
                        AID
                    </div>
                    <div className="truncate text-sm font-semibold text-ink">{document.id}</div>
                </div>
                <div className="min-w-0 @lg:col-auto">
                    <div className="hidden truncate text-sm text-ink @lg:block">
                        {document.documentId}
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1.5 @lg:hidden">
                        <span
                            className={[
                                "inline-flex items-center rounded-pill px-2 py-0.5 text-[11px] font-medium",
                                statusClass(document.status),
                            ].join(" ")}
                        >
                            {t(`attributes.data_entering.units.statuses.${document.status}`)}
                        </span>
                    </div>
                </div>
                <div className="col-span-2 min-w-0 @lg:col-auto">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted @lg:hidden">
                        <span>
                            <span className="text-[10px] uppercase tracking-wide">UOLU </span>
                            <span className="text-ink">{document.documentId}</span>
                        </span>
                        <span>
                            <span className="text-[10px] uppercase tracking-wide">
                                {t("attributes.account.code")}{" "}
                            </span>
                            <span className="text-ink">{document?.account?.code}</span>
                        </span>
                        <span>{moment(document.time).locale(locale).fromNow()}</span>
                    </div>
                    <div className="hidden truncate text-sm text-ink @lg:block">
                        {document?.account?.code}
                    </div>
                </div>
                <div className="hidden items-center justify-end gap-2 @lg:flex">
                    <span className="text-xs text-ink-muted">
                        {moment(document.time).locale(locale).fromNow()}
                    </span>
                    <span
                        className={[
                            "inline-flex items-center rounded-pill px-2 py-0.5 text-[11px] font-medium",
                            statusClass(document.status),
                        ].join(" ")}
                    >
                        {t(`attributes.data_entering.units.statuses.${document.status}`)}
                    </span>
                </div>
            </div>
        </button>
    );
}
